import {useState} from 'react';

import {Dialog} from 'primereact/dialog';
import {Button} from 'primereact/button';

import {ToastService} from '../../AppToast';
import {translate, translateError} from '../language';
import {isAsyncFlowCancelled} from '../utils/asyncFlow';
import {useTranslation} from '../language';

import {useForm, Field} from './Form';

interface DialogCrupProps {
  header: string;
  dataKey?: string;
  width: string;
  fields: Array<Field>;

  defaultValue?: any;

  createItem?(item: any): Promise<any>;

  updateItem?(id: string, item: any): Promise<any>;

  refreshLazyData?(data?: any): void;

  layout?: string;
  formClassName?: string;
  submitLabel?: string;
  onValidationError?(): void;
}

export const useDialogCrup = (props: DialogCrupProps) => {
  const {t} = useTranslation();

  const {header, dataKey, width, fields, defaultValue, createItem, updateItem, refreshLazyData, layout, formClassName, submitLabel, onValidationError} = props;
  const resolvedSubmitLabel = submitLabel || t('common.save');

  const [idUpdate, setIdUpdate] = useState(null);
  const [idClone, setIdClone] = useState(null);
  const [idView, setIdView] = useState(null);
  const [display, setDisplay] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');
  const [submitError, setSubmitError] = useState('');

  const {render: renderForm, reset, setValue, valid: formValid, getValue, touched} = useForm({
    fields,
    idUpdate,
    layout,
    formClassName
  });

  const create = (_defaultValue?: any) => {
    reset();
    setValidationMessage('');
    setSubmitError('');
    if (_defaultValue) {
      setValue(_defaultValue);
    } else if (defaultValue) {
      setValue(defaultValue);
    }
    setIdUpdate(null);
    setIdClone(null);
    setDisplay(true);
  }

  const update = (item: any) => {
    reset();
    setValidationMessage('');
    setSubmitError('');
    setValue(item);
    setIdUpdate(item[dataKey]);
    setIdClone(null);
    setDisplay(true);
  }

  const clone = (item: any) => {
    reset();
    setValidationMessage('');
    setSubmitError('');
    setValue(item);
    setIdUpdate(null);
    setIdClone(item[dataKey]);
    setDisplay(true);
  }

  const view = (item: any) => {
    reset();
    setValidationMessage('');
    setSubmitError('');
    setValue(item);
    setIdView(item);
    setDisplay(true);
  }

  const handleSubmit = () => {
    if (submitting) return;
    if (!formValid()) {
      setValidationMessage(t('common.requiredFieldsMessage'));
      onValidationError?.();
      if (!onValidationError) {
        ToastService.error(t('common.requiredFieldsMessage'));
      }
      return;
    }
    setValidationMessage('');
    setSubmitError('');
    if (!submitting) {
      setSubmitting(true);

      let promise;
      try {
        if (idUpdate) {
          promise = updateItem(idUpdate, getValue());
        } else {
          promise = createItem(getValue());
        }
      } catch (error) {
        setSubmitError(t('common.taxAuthorityConnectionFailed'));
        ToastService.error(t('common.taxAuthorityConnectionFailed'));
        setSubmitting(false);
        return;
      }

      Promise.resolve(promise).then((data) => {
        setDisplay(false);
        ToastService.success();
        refreshLazyData(data);
      }).catch((error) => {
        if (isAsyncFlowCancelled(error)) {
          return;
        }
        setSubmitError(t('common.taxAuthorityConnectionFailed'));
        // Prevent React error overlay on rejected promise (e.g. API 500)
        ToastService.error(translateError(error?.response || error));
      }).finally(() => {
        setSubmitting(false);
      });
    }
  }

  const render = () => {
    let _header;
    if (header && header[0] === '!') {
      _header = translate(header.substring(1));
    } else {
      const translatedHeader = translate(header);
      _header = `${t('common.create')} ${translatedHeader}`;
      if (idClone) {
        _header = `${t('common.copy')} ${translatedHeader}`;
      }
      if (idUpdate) {
        _header = `${t('common.edit')} ${translatedHeader}`;
      }
    }
    return (
      <Dialog header={_header} visible={display} style={{width}} position="top" onHide={() => setDisplay(false)}>

        {(validationMessage || submitError) && (
          <div className="p-message p-component p-message-error mb-2" role="alert">
            <div className="p-message-wrapper">
              <span className="p-message-icon pi pi-times-circle"></span>
              <span className="p-message-detail">{validationMessage || submitError}</span>
            </div>
          </div>
        )}
        {renderForm()}

        <div className="flex justify-content-end">
          <Button label={t('common.close')} icon="pi pi-times" onClick={() => setDisplay(false)} className="p-button-outlined p-button-secondary p-button-sm mr-2"/>
          {!idView &&
						<Button label={resolvedSubmitLabel} icon="pi pi-check" onClick={handleSubmit} loading={submitting} className="p-button-sm"/>
          }
        </div>
      </Dialog>
    );
  }

  return {render, create, update, clone, view, getValue, setValue, touched};
}
